import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import analyticsService from '../Services/AnalyticsService';
import { getCategorySummary, getCycleMs, getTechSummary, tsToMs } from '../platform/history/serviceRecord';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('30');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Fetching data with time range:', timeRange);
        setIsLoading(true);
        const rawData = await analyticsService.fetchData(timeRange);
        
        console.log('Raw data received:', {
          trucksCount: rawData.trucks?.length,
          techniciansCount: rawData.technicians?.length
        });
        
        const rangeDays = Number(timeRange);
        const cutoff = Number.isFinite(rangeDays)
          ? Date.now() - rangeDays * 24 * 60 * 60 * 1000
          : null;
        const completedTrucks = (rawData.trucks || []).filter(truck => {
          if (truck.status !== 'completed') return false;
          if (!truck.startTime || !truck.completionTime) return false;
          if (!getCycleMs(truck)) return false;
          const closedAt = tsToMs(truck.completionTime);
          return !cutoff || (closedAt && closedAt >= cutoff);
        });
        const voidedTrucks = (rawData.trucks || []).filter(truck => truck.status === 'voided');

        // Completed-job category metrics
        const categoryMetrics = completedTrucks.reduce((acc, truck) => {
          const categories = getCategorySummary(truck);
          const names = categories.length ? categories : ['Uncategorized'];
          const repairDuration = getCycleMs(truck) || 0;

          names.forEach(category => {
            const existing = acc.find(item => item.category === category);
            if (existing) {
              existing.count++;
              if (truck.warranty) {
                existing.warrantyCount = (existing.warrantyCount || 0) + 1;
              }
              existing.totalCompletionTime += repairDuration;
            } else {
              acc.push({
                category,
                count: 1,
                warrantyCount: truck.warranty ? 1 : 0,
                totalCompletionTime: repairDuration
              });
            }
          });
          
          return acc;
        }, []);

        console.log('Processed category metrics:', categoryMetrics);

        // Calculate processed metrics with fallbacks
        const processedCategoryMetrics = categoryMetrics.map(cat => ({
          ...cat,
          avgCompletionTime: cat.count ? 
            Math.abs(cat.totalCompletionTime / cat.count) / (60 * 60 * 1000) : 0,
          warrantyRate: cat.count ? 
            (cat.warrantyCount / cat.count) * 100 : 0
        })).sort((a, b) => b.count - a.count);

        // Technician Performance
        const technicianPerformance = rawData.technicians.map(tech => {
          const techTrucks = completedTrucks.filter(truck =>
            getTechSummary(truck).map(String).includes(String(tech.number))
          );
          const totalTime = techTrucks.reduce((total, truck) => total + (getCycleMs(truck) || 0), 0);
        
          return {
            name: tech.name,
            totalRepairs: techTrucks.length,
            completedRepairs: techTrucks.length,
            averageCompletionTime: techTrucks.length 
              ? totalTime / techTrucks.length / (60 * 60 * 1000)
              : 0,
            specialties: tech.specialties || []
          };
        }).sort((a, b) => b.totalRepairs - a.totalRepairs);

        // Warranty Metrics with Fallbacks
        const warrantyMetrics = {
          total: completedTrucks.length,
          warrantyCount: completedTrucks.filter(t => t.warranty).length,
          warrantyRate: completedTrucks.length > 0
            ? (completedTrucks.filter(t => t.warranty).length / completedTrucks.length) * 100
            : 0
        };

        console.log('Final processed data:', {
          categoryMetrics: processedCategoryMetrics,
          technicianPerformance,
          warrantyMetrics
        });

        setData({
          trucks: completedTrucks,
          voidedTrucks,
          technicians: rawData.technicians,
          categoryMetrics: processedCategoryMetrics,
          technicianPerformance,
          warrantyMetrics
        });
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [timeRange]);

  // Loading State
  if (isLoading) {
    return (
      <div className="tt-page">
        <div className="tt-page-header">
          <div>
            <h1 className="tt-page-title">Operations Intelligence</h1>
            <p className="tt-page-subtitle">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="tt-page">
        <div className="tt-card tt-list-card">
        <h2 className="tt-card-title">Error Loading Intelligence</h2>
        <p>{error}</p>
        </div>
      </div>
    );
  }

  // No Data State
  if (!data || !data.trucks || data.trucks.length === 0) {
    return (
      <div className="tt-page">
        <div className="tt-page-header">
          <div>
            <h1 className="tt-page-title">Operations Intelligence</h1>
            <p className="tt-page-subtitle">No repair data available</p>
          </div>
        </div>
        <div className="tt-card tt-list-card">
          <p className="tt-empty">Try adjusting the time range or check if repairs have been recorded.</p>
          <label>Time Range</label>
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            className="tt-select"
            style={{ maxWidth: '220px' }}
          >
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="90">Last 90 Days</option>
          </select>
        </div>
      </div>
    );
  }
  //Render statement
  return (
    <div className="tt-page">
      <div className="tt-page-header">
        <div>
          <h1 className="tt-page-title">Operations Intelligence</h1>
          <p className="tt-page-subtitle">Live service load, workflow health, and operational performance</p>
        </div>
        <div className="tt-toolbar" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => navigate('/reports/cycle-time')}
              style={{ padding: '7px 14px', background: '#0B2265', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Cycle Time Report →
            </button>
            <label>Time Range</label>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="tt-select"
              style={{ width: '160px' }}
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
            </select>
        </div>
      </div>

      <div className="tt-metric-grid">
        {[
          { 
            title: 'Completed Repairs', 
            value: data.warrantyMetrics.total,
            tone: 'blue'
          },
          {
            title: 'Voided Jobs',
            value: data.voidedTrucks.length,
            tone: 'red'
          },
          { 
            title: 'Warranty Repairs', 
            value: data.warrantyMetrics.warrantyCount,
            tone: 'green'
          },
          { 
            title: 'Warranty Rate', 
            value: `${data.warrantyMetrics.warrantyRate.toFixed(1)}%`,
            tone: 'gold'
          }
        ].map(({ title, value, tone }) => (
          <div 
            key={title} 
            className={`tt-metric-card tt-metric-card--${tone}`}
          >
            <h2>{title}</h2>
            <p>{value}</p>
          </div>
        ))}
      </div>

      <div className="tt-analytics-stack">
        <div className="tt-card tt-analytics-panel">
          <h2 className="tt-section-title">Repairs by Category</h2>
          <div className="tt-chart-wrap">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.categoryMetrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#8884d8" name="Total Repairs" />
                <Bar dataKey="warrantyCount" fill="#82ca9d" name="Warranty Repairs" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="tt-card tt-analytics-panel">
          <h2 className="tt-section-title">Repair Category Distribution</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data.categoryMetrics}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius="80%"
                fill="#8884d8"
                dataKey="count"
                nameKey="category"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
              >
                {data.categoryMetrics.map((entry, index) => (
                  <Cell key={`cell-${entry.category}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="tt-card tt-analytics-panel">
          <h2 className="tt-section-title">Category Breakdown</h2>
          <div className="tt-table-wrap">
            <table className="tt-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Total</th>
                  <th>Warranty</th>
                  <th>Rate</th>
                  <th>Avg Time</th>
                </tr>
              </thead>
              <tbody>
                {data.categoryMetrics.map((category, index) => (
                  <tr key={index}>
                    <td>{category.category}</td>
                    <td>{category.count}</td>
                    <td>{category.warrantyCount}</td>
                    <td>{category.warrantyRate.toFixed(1)}%</td>
                    <td>{category.avgCompletionTime.toFixed(1)}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="tt-card tt-analytics-panel">
          <h2 className="tt-section-title">Technician Performance</h2>
          <div className="tt-table-wrap">
            <table className="tt-table">
              <thead>
                <tr>
                  <th>Technician</th>
                  <th>Total</th>
                  <th>Completed</th>
                  <th>Avg Time</th>
                  <th>Specialties</th>
                </tr>
              </thead>
              <tbody>
                {data.technicianPerformance.map((tech, index) => (
                  <tr key={index}>
                    <td>{tech.name}</td>
                    <td>{tech.totalRepairs}</td>
                    <td>{tech.completedRepairs}</td>
                    <td>{tech.averageCompletionTime.toFixed(1)}h</td>
                    <td>{tech.specialties.join(', ') || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
